import { bezSierotek } from '@insolitum/ui';
import { Eye, EyeOff, AlertCircle, Mail, Check, Play, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';

import { AuroraNeuroCanvas } from '../../components/aurora/AuroraNeuroCanvas';
import {
  LINK_PARTNERA,
  POLE_WEJSCIA,
  PrzelacznikJezykaWejscia,
  TEKST_NA_POSWIACIE,
} from '../../components/marka/UkladWejscia';
import { ZnakWejsciaPionowy, ZnakWejsciaPoziomy } from '../../components/marka/ZnakWejscia';
import { useAppMode } from '../../hooks/useAppMode';
import { languages } from '../../i18n';
import { pobierzPowodWylogowania } from '../../lib/martwaSesja';
import { useAuth } from '../../providers/AuthProvider';

const SLIDE_COUNT = 3;
const SLIDE_INTERVAL_MS = 8000;

export function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Powód, dla którego powłoka sama zakończyła sesję (lib/martwaSesja.ts) —
  // jedno zdanie zamiast „Unauthorized" pod przyciskiem, który nie zawinił.
  const [powodWylogowania] = useState(() => pobierzPowodWylogowania());

  // Karuzela hero (domyślny → reklama modułu → showreel) — jak w prototypie:
  // auto co 8 s, klik w kropkę resetuje timer, reduced-motion wyłącza auto.
  const [slide, setSlide] = useState(0);
  const slideTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    slideTimer.current = setInterval(
      () => setSlide((s) => (s + 1) % SLIDE_COUNT),
      SLIDE_INTERVAL_MS
    );
    return () => {
      if (slideTimer.current) clearInterval(slideTimer.current);
    };
  }, []);
  const goSlide = (i: number) => {
    if (slideTimer.current) clearInterval(slideTimer.current);
    setSlide(i);
    if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
      slideTimer.current = setInterval(
        () => setSlide((s) => (s + 1) % SLIDE_COUNT),
        SLIDE_INTERVAL_MS
      );
    }
  };

  const { signIn, isAuthenticated } = useAuth();
  const { isSuperAdminDomain } = useAppMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const from = (location.state as any)?.from?.pathname || '/';

  // Link zaproszenia partnera (`inviteUserByEmail`) wraca na
  // `/login?partner_activation=true` z już utworzoną sesją (GoTrue verify), a
  // konto NIE MA hasła. Do tej pory ekran pokazywał formularz logowania pytający
  // o hasło, którego nie ma. Zalogowanego odsyłamy: przy aktywacji na
  // `/reset-password` (ustawienie pierwszego hasła), inaczej tam, skąd przyszedł.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (searchParams.get('partner_activation') === 'true') {
      navigate('/reset-password', { replace: true });
    } else {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, searchParams, from, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await signIn(email, password);
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message || t('auth.invalidCredentials'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={`aurora-grain relative min-h-screen bg-aurora-bg bg-[radial-gradient(120%_90%_at_30%_10%,rgba(var(--ids-brand-rgb),0.22),transparent_55%),radial-gradient(90%_80%_at_90%_100%,rgba(139,92,246,0.18),transparent_50%)] lg:grid lg:grid-cols-[1.05fr_0.95fr] ${TEKST_NA_POSWIACIE}`}
    >
      {/* Żywa sieć — animowane tło całego ekranu, także na mobile */}
      <div className="auth-hero__orbit" aria-hidden="true">
        <AuroraNeuroCanvas />
      </div>

      {/* Przełącznik języka — to samo miejsce na całej rodzinie ekranów
          wejścia: desktop na osi środka logo z marginesem 48 = lewemu,
          telefon w rogu 24/24. */}
      <PrzelacznikJezykaWejscia />

      {/* Hero panel (desktop) — karuzela: hero → promo Nexus AI → showreel */}
      <div className="relative z-10 hidden lg:flex flex-col justify-between p-12">
        {/* Znak z hasłem z tego samego komponentu, co na pozostałych ekranach
            wejścia (`ZnakWejscia`) — róg (48, 48) z `p-12`, więc przejście
            logowanie → rejestracja nie zmienia lockupu ani jego miejsca. */}
        <ZnakWejsciaPoziomy />

        <div className="relative flex-1 flex flex-col min-h-[320px]">
          {/* SLAJD 1 — domyślny hero */}
          <div className={`auth-slide ${slide === 0 ? 'is-on' : ''}`}>
            <div className="max-w-md">
              <h2 className="grad-text font-display text-4xl font-bold leading-[1.05] tracking-tight">
                System nerwowy
                <br />
                Twojego biznesu.
              </h2>
              {/* Polska typografia: „i", „w", „z" nie zostają na końcu wiersza.
                  Akapit stoi wprost na płótnie żywej sieci (bez karty): w ciemnym
                  stonowany #94A3B8 miał na najgorszej klatce płótna 4,14:1, więc
                  tu jest cosmos-200 (≥ 7:1 na tej samej klatce). Jasny bez zmian
                  (`TEKST_NA_POSWIACIE` na korzeniu). */}
              <p className="text-aurora-muted [[data-mode=dark]_&]:text-cosmos-200 text-[15px] leading-relaxed mt-4 max-w-sm">
                {bezSierotek(
                  'Czujniki, ludzie, maszyny, zamówienia i decyzje AI — w jednej, żywej platformie. Centrum dowodzenia z przyszłości.'
                )}
              </p>
            </div>
          </div>

          {/* SLAJD 2 — reklama modułu */}
          <div className={`auth-slide ${slide === 1 ? 'is-on' : ''}`}>
            <div className="auth-promo">
              <div className="auth-promo__top">
                <span className="auth-promo__ico">
                  <Sparkles />
                </span>
                <div>
                  <div className="auth-promo__name">Nexus AI</div>
                  <div className="auth-promo__cat">
                    {bezSierotek('Silnik decyzyjny · nowość w Marketplace')}
                  </div>
                </div>
                <span className="badge badge--brand ml-auto">NOWE</span>
              </div>
              <p className="auth-promo__copy">
                {bezSierotek(
                  'AI, które zna Twój biznes: podejmuje decyzje, pisze poprawki, pilnuje HACCP i mówi ludzkim głosem.'
                )}
              </p>
              {/* Statystyki promo (wartości demo jak w prototypie V2) */}
              <div className="auth-promo__stats">
                <div>
                  <b>1 284</b>
                  <span>{t('auth.promoStatDecisions', 'decyzji / dzień')}</span>
                </div>
                <div>
                  <b>96,2%</b>
                  <span>{t('auth.promoStatAccuracy', 'trafność')}</span>
                </div>
                <div>
                  <b>2,4 €</b>
                  <span>{t('auth.promoStatCost', 'koszt / tenant')}</span>
                </div>
              </div>
              <div className="auth-promo__cta">
                <span className="btn btn--primary btn--sm">
                  {t('auth.promoTrialCta', 'Aktywuj trial 14 dni')}
                </span>
                <span className="muted text-xs">
                  {t('auth.promoNoCard', 'bez karty · anuluj kiedy chcesz')}
                </span>
              </div>
            </div>
          </div>

          {/* SLAJD 3 — showreel */}
          <div className={`auth-slide ${slide === 2 ? 'is-on' : ''}`}>
            <div className="auth-ad auth-ad--video">
              <div className="auth-video__ph">
                <span className="auth-video__play">
                  <Play className="w-6 h-6 fill-current" />
                </span>
                <span className="auth-video__t">Insolitum w 60 sekund</span>
                <span className="auth-video__meta">
                  <span className="live text-[10px]">
                    <span className="pulse" />
                    Showreel
                  </span>
                  · Globe · kiosk · voice · druk
                </span>
              </div>
              {/* Pasek postępu showreela — dekoracyjny (auth-video__bar z prototypu) */}
              <div className="auth-video__bar" aria-hidden="true">
                <i />
              </div>
            </div>
            <p className="text-aurora-muted [[data-mode=dark]_&]:text-cosmos-200 text-[15px] leading-relaxed mt-2 max-w-md">
              {bezSierotek('Od czujnika na hali po decyzję AI — jeden system, jedna rozmowa.')}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-6">
          <span className="flex items-center gap-2 text-[12.5px] text-aurora-muted">
            <Check className="w-4 h-4 text-aurora-brand" />
            WCAG 2.2 AA
          </span>
          <span className="flex items-center gap-2 text-[12.5px] text-aurora-muted">
            <Check className="w-4 h-4 text-aurora-brand" />
            Offline-first
          </span>
          <span className="flex items-center gap-2 text-[12.5px] text-aurora-muted">
            <Check className="w-4 h-4 text-aurora-brand" />
            {/* Z konfiguracji i18n, nie wpisane: lista języków obiecana klientowi
                nie może wyprzedzić tłumaczeń, które naprawdę istnieją. */}
            {languages.map((l) => l.code.toUpperCase()).join(' · ')}
          </span>
          {/* Nieaktywne kropki: `--ids-border-strong` miał 1,4:1 (jasny)
              i 1,48:1 (ciemny) — grafika kontrolki potrzebuje 3:1. Bieżąca
              zostaje w `--ids-brand` i szersza (22 px), więc różni się
              kształtem, nie tylko kolorem. */}
          <span className="auth-dots" role="tablist" aria-label="Slajdy">
            {[0, 1, 2].map((i) => (
              <button
                key={i}
                type="button"
                role="tab"
                className="auth-dot [&:not([aria-selected=true])]:bg-aurora-subtle [[data-mode=light]_&:not([aria-selected=true])]:bg-cosmos-400"
                aria-selected={slide === i}
                aria-label={`Slajd ${i + 1}`}
                onClick={() => goSlide(i)}
              />
            ))}
          </span>
        </div>
      </div>

      {/* Auth panel — na telefonie od góry (znak 24 px od górnej krawędzi
          niezależnie od wysokości ekranu, jak w `UkladWejscia`), od `lg`
          formularz na środku kolumny. Dół 24 px, symetrycznie do góry:
          przy 48 px logowanie na telefonie 390 × 844 przewijało się o 21 px
          (zmierzone: scrollHeight 865), a ekran z jednym formularzem nie ma
          prawa się przewijać. */}
      <div className="relative z-10 flex min-h-screen flex-col items-center justify-start px-6 pb-[calc(1.5rem_+_env(safe-area-inset-bottom))] pt-[calc(1.5rem_+_env(safe-area-inset-top))] lg:justify-center lg:p-10">
        <div className="w-full max-w-[440px]">
          {/* Znak z hasłem (mobile) — ten sam komponent, co na pozostałych
              ekranach wejścia (`ZnakWejscia`), więc przejście do resetu hasła
              albo rejestracji nie rusza znaku. Nagłówek 24 px pod hasłem
              (hasło należy do znaku: 16 px nad nim, 24 px pod nim), w tej
              samej interlinii co tytuł ekranu rodziny (1,15) — pierwszy tekst
              pod znakiem stoi na tym samym y, co tytuł rejestracji. */}
          <div className="lg:hidden flex flex-col items-center text-center mb-6">
            <ZnakWejsciaPionowy />
            <h2 className="grad-text font-display text-2xl font-bold leading-[1.15] tracking-tight text-balance mt-6">
              System nerwowy Twojego biznesu.
            </h2>
          </div>

          {/* Karta logowania stoi nad płótnem żywej sieci (`.auth-hero__orbit`
              to cały ekran, rdzeń sieci świeci w jego środku — na telefonie
              pod polem e-mail). `.authcard` z Aurory to szkło 2,5 % bieli
              (w jasnym 62 %): na prawdziwym płótnie ramka pola e-mail
              w ciemnym miała 2,67:1, „Witaj ponownie…” 3,8:1, a na desktopie
              „Chcesz zostać partnerem?” 3,5:1. Tu karta jest prawie kryjąca —
              85 % `--ids-surface` w obu trybach, z rozmyciem z Aurory pod
              spodem — lokalnie, bez ruszania `.authcard` dla reszty systemu.
              Wariant `[[data-mode]_&]:` (0,2,0, warstwa narzędzi po
              komponentach) wygrywa także z jasną regułą Aurory
              `[data-mode="light"] .authcard`. Bramka: najgorsza klatka płótna
              w `ekranyWejsciaWChromium.test.tsx`. */}
          <div className="authcard [[data-mode]_&]:[background-color:color-mix(in_srgb,var(--ids-surface)_85%,transparent)]">
            <h1 className="authcard__h">{t('auth.signIn')}</h1>
            <p className="authcard__s">
              {t('auth.welcomeBack', 'Witaj ponownie. Wejdź do panelu organizacji.')}
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              {powodWylogowania === 'martwa_sesja' && !error && (
                <div
                  role="status"
                  data-testid="powod-wylogowania"
                  className="flex items-center gap-2 p-3 bg-aurora-warning/10 border border-aurora-warning/20 rounded-ids-md text-aurora-warning [[data-mode=light]_&]:text-amber-800 text-sm"
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {t(
                    'auth.sesjaZakonczona',
                    'Sesja została zakończona (np. wylogowanie na innym urządzeniu albo wygaśnięcie). Zaloguj się ponownie.'
                  )}
                </div>
              )}
              {error && (
                <div
                  role="alert"
                  className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-ids-md text-red-400 [[data-mode=light]_&]:text-red-800 text-sm"
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {bezSierotek(error)}
                </div>
              )}

              <div className="field">
                <label htmlFor="email" className="field__label">
                  {t('auth.email')}
                </label>
                <div className="inputwrap">
                  <Mail />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className={`input ${POLE_WEJSCIA}`}
                    placeholder={t('auth.emailPlaceholder')}
                  />
                </div>
              </div>

              <div className="field">
                <label htmlFor="password" className="field__label">
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className={`input pr-12 ${POLE_WEJSCIA}`}
                    placeholder={t('auth.passwordPlaceholder')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Ukryj hasło' : 'Pokaż hasło'}
                    aria-pressed={showPassword}
                    className="absolute right-1 top-1/2 -translate-y-1/2 iconbtn text-aurora-subtle"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                {/* Tor wyłączonego przełącznika: `--ids-border-strong` dawał
                    1,46:1 do karty (WCAG 1.4.11 chce 3:1), więc tor ma kolor
                    ramki pól wejścia — biały suwak na nim też jest widoczny.
                    Włączony bierze `--ids-brand` z Aurory (0,3,1 > 0,2,0). */}
                <label className="switch">
                  <input type="checkbox" />
                  <span className="switch__track bg-aurora-muted [[data-mode=light]_&]:bg-cosmos-400" />
                  <span className="text-[13px] text-aurora-muted">{t('auth.rememberMe')}</span>
                </label>
                <Link
                  to="/forgot-password"
                  className="text-[13px] font-semibold text-[color:var(--ids-brand-text)] hover:underline"
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>

              <button type="submit" disabled={isLoading} className="btn btn--primary btn--block">
                {isLoading ? <span className="spinner" /> : t('auth.signIn')}
              </button>
            </form>

            <div className="mt-6 h-px bg-aurora-border" aria-hidden="true" />

            <div className="mt-6 text-center space-y-2">
              <p className="text-aurora-muted text-sm">
                {t('auth.noAccount')}{' '}
                {/* `--ids-brand-text`, nie `--ids-brand`: na karcie nad poświatą
                    w ciemnym blue-500 dawał 4,37:1, jaśniejszy stopień tekstu
                    brandowego ≥ 6:1 (w jasnym to ten sam blue-600). */}
                <Link
                  to="/register"
                  className="text-[color:var(--ids-brand-text)] hover:underline font-medium"
                >
                  {t('auth.createAccount')}
                </Link>
              </p>
              {!isSuperAdminDomain && (
                <p className="text-aurora-subtle text-xs">
                  {t('auth.becomePartnerQuestion', 'Chcesz zostać partnerem?')}{' '}
                  <Link
                    to="/register/partner"
                    className={`${LINK_PARTNERA} font-medium transition-colors`}
                  >
                    {t('auth.joinPartnerProgram', 'Dołącz do programu partnerskiego')}
                  </Link>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
